import React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Calendar, Clock, Users, Lock } from "lucide-react"
import { useMeetings } from "@/hooks/useMeetings"
import { useForm } from "react-hook-form"
import { format } from "date-fns"

interface CriarReuniaoModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface FormData {
  title: string
  description: string
  scheduled_date: string
  scheduled_time: string
  duration_minutes: number
  max_participants: number
  meeting_password: string
  is_recorded: boolean
}

export function CriarReuniaoModal({ open, onOpenChange }: CriarReuniaoModalProps) {
  const { createMeeting, isCreating } = useMeetings()
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      duration_minutes: 60,
      max_participants: 50,
      is_recorded: false
    }
  })

  const onSubmit = (data: FormData) => {
    // Combine date and time
    const scheduledDateTime = new Date(`${data.scheduled_date}T${data.scheduled_time}`)
    
    createMeeting({
      title: data.title,
      description: data.description,
      scheduled_date: scheduledDateTime.toISOString(),
      duration_minutes: data.duration_minutes,
      max_participants: data.max_participants,
      meeting_password: data.meeting_password || undefined,
      is_recorded: data.is_recorded,
      status: "scheduled",
    })
    
    reset()
    onOpenChange(false)
  }

  // Get minimum date (today)
  const today = format(new Date(), "yyyy-MM-dd")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Nova Reunião Online</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Título da Reunião *</Label>
            <Input
              id="title"
              placeholder="Ex: Assembleia Ordinária - Janeiro 2024"
              {...register("title", { required: "Título é obrigatório" })}
            />
            {errors.title && (
              <p className="text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              placeholder="Descreva a pauta e objetivos da reunião..."
              rows={3}
              {...register("description")}
            />
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduled_date" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Data *
              </Label>
              <Input
                id="scheduled_date"
                type="date"
                min={today}
                {...register("scheduled_date", { required: "Data é obrigatória" })}
              />
              {errors.scheduled_date && (
                <p className="text-sm text-red-600">{errors.scheduled_date.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="scheduled_time" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Horário *
              </Label>
              <Input
                id="scheduled_time"
                type="time"
                {...register("scheduled_time", { required: "Horário é obrigatório" })}
              />
              {errors.scheduled_time && (
                <p className="text-sm text-red-600">{errors.scheduled_time.message}</p>
              )}
            </div>
          </div>

          {/* Duration and Participants */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="duration_minutes" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Duração (minutos)
              </Label>
              <Input
                id="duration_minutes"
                type="number"
                min="15"
                max="480"
                {...register("duration_minutes", { 
                  valueAsNumber: true,
                  min: { value: 15, message: "Mínimo 15 minutos" },
                  max: { value: 480, message: "Máximo 8 horas" }
                })}
              />
              {errors.duration_minutes && (
                <p className="text-sm text-red-600">{errors.duration_minutes.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_participants" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Máx. Participantes
              </Label>
              <Input
                id="max_participants"
                type="number"
                min="2"
                max="100"
                {...register("max_participants", { 
                  valueAsNumber: true,
                  min: { value: 2, message: "Mínimo 2 participantes" },
                  max: { value: 100, message: "Máximo 100 participantes" }
                })}
              />
              {errors.max_participants && (
                <p className="text-sm text-red-600">{errors.max_participants.message}</p>
              )}
            </div>
          </div>

          {/* Optional Settings */}
          <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Configurações Opcionais
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="meeting_password" className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Senha da Reunião
              </Label>
              <Input
                id="meeting_password"
                type="password"
                placeholder="Deixe em branco para acesso livre"
                {...register("meeting_password")}
              />
              <p className="text-xs text-muted-foreground">
                Opcional: senha para proteger a reunião
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <input
                id="is_recorded"
                type="checkbox"
                className="rounded border-gray-300"
                {...register("is_recorded")}
              />
              <Label htmlFor="is_recorded" className="text-sm font-normal">
                Gravar reunião automaticamente
              </Label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={isCreating}
              className="bg-primary hover:bg-primary/90"
            >
              {isCreating ? "Criando..." : "Criar Reunião"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}